/**
 * Encode/decode itinerary items to/from iOS Device Sync item dict shape.
 */

import type { ItineraryItem, ItineraryItemType } from '../../types/client';
import {
  durationMinutesFromMetadata,
  minutesToMetadataDurationSeconds,
} from '../../utils/durationUnits';
import type {
  DeviceSyncAttachment,
  DeviceSyncBarcode,
  DeviceSyncItem,
  DeviceSyncItemOption,
  DeviceSyncLocation,
  DeviceSyncReminder,
} from './types';

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function strOrNull(value: unknown): string | null {
  if (value == null) return null;
  const s = String(value);
  return s.length > 0 ? s : null;
}

function numOrNull(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function dateOnlyFromIso(iso: string | null | undefined): string | null {
  if (!iso) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(iso)) return iso.slice(0, 10);
  return iso;
}

/** HH:mm from an ISO timestamp (UTC clock — mirrors export clock fields for round-trip). */
export function hmFromIso(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined;
  const m = iso.match(/T(\d{2}):(\d{2})/);
  if (!m) return undefined;
  return `${m[1]}:${m[2]}`;
}

/** Duration minutes between two ISO timestamps. */
export function durationMinutesFromIso(
  start: string | null | undefined,
  end: string | null | undefined
): number | undefined {
  if (!start || !end) return undefined;
  const a = Date.parse(start);
  const b = Date.parse(end);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b <= a) return undefined;
  return Math.round((b - a) / 60000);
}

/**
 * Build ISO start/end from day date (YYYY-MM-DD) + HH:mm + optional duration.
 * Uses Z suffix for Device Sync wire (matches fixture style).
 */
export function isoRangeFromDayTime(
  dayDate: string,
  timeHm: string | undefined,
  durationMinutes: number | undefined
): { startTime: string | null; endTime: string | null } {
  if (!timeHm || !/^\d{1,2}:\d{2}/.test(timeHm)) {
    return { startTime: null, endTime: null };
  }
  const [hRaw, mRaw] = timeHm.split(':');
  const h = String(Number(hRaw)).padStart(2, '0');
  const m = String(Number(mRaw)).padStart(2, '0');
  const startTime = `${dayDate}T${h}:${m}:00Z`;
  if (durationMinutes == null || !Number.isFinite(durationMinutes) || durationMinutes <= 0) {
    return { startTime, endTime: null };
  }
  const startMs = Date.parse(startTime);
  if (!Number.isFinite(startMs)) return { startTime, endTime: null };
  const endTime = new Date(startMs + durationMinutes * 60000).toISOString().replace(/\.\d{3}Z$/, 'Z');
  return { startTime, endTime };
}

export function encodeLocation(
  location: ItineraryItem['location'] | undefined,
  legacyAddress?: string
): DeviceSyncLocation | null {
  const address = (location?.address ?? legacyAddress ?? '').trim();
  if (!address && location?.latitude == null && location?.longitude == null) {
    return null;
  }
  const out: DeviceSyncLocation = {
    id: location?.id || newId(),
    address: address || '',
  };
  if (location?.latitude != null && Number.isFinite(location.latitude)) {
    out.latitude = location.latitude;
  }
  if (location?.longitude != null && Number.isFinite(location.longitude)) {
    out.longitude = location.longitude;
  }
  return out;
}

export function decodeLocation(raw: unknown): {
  location?: ItineraryItem['location'];
  address?: string;
} {
  if (raw == null) return {};
  const o = asRecord(raw);
  const address = strOrNull(o.address) ?? '';
  const lat = numOrNull(o.latitude);
  const lng = numOrNull(o.longitude);
  const location: ItineraryItem['location'] = { address };
  if (lat != null) location.latitude = lat;
  if (lng != null) location.longitude = lng;
  return { location, address: address || undefined };
}

function encodeReminders(raw: unknown): DeviceSyncReminder[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((r) => {
    const o = asRecord(r);
    return {
      id: String(o.id ?? newId()),
      minutesBefore: numOrNull(o.minutesBefore) ?? 0,
    };
  });
}

function encodeAttachments(raw: unknown): DeviceSyncAttachment[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((r) => {
    const o = asRecord(r);
    return {
      id: String(o.id ?? newId()),
      fileName: String(o.fileName ?? ''),
      fileSize: numOrNull(o.fileSize) ?? 0,
      mimeType: String(o.mimeType ?? ''),
      createdAt: String(o.createdAt ?? ''),
    };
  });
}

function encodeItemOptions(raw: unknown): DeviceSyncItemOption[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((r, index) => {
    const o = asRecord(r);
    return {
      id: String(o.id ?? newId()),
      itemDescription: strOrNull(o.itemDescription ?? o.title),
      optionDescription: strOrNull(o.optionDescription ?? o.description),
      url: strOrNull(o.url),
      address: strOrNull(o.address),
      isDone: Boolean(o.isDone ?? o.completed),
      order: numOrNull(o.order) ?? index,
    };
  });
}

function encodeBarcode(raw: unknown): DeviceSyncBarcode | null {
  if (raw == null) return null;
  const o = asRecord(raw);
  if (!o.type && !o.payload) return null;
  return {
    type: String(o.type ?? ''),
    payload: String(o.payload ?? ''),
    imagePath: strOrNull(o.imagePath),
    imageUrl: strOrNull(o.imageUrl),
    imageHint: strOrNull(o.imageHint),
  };
}

function encodeMetadata(raw: unknown): Record<string, string> {
  const o = asRecord(raw);
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(o)) {
    if (v == null) continue;
    out[k] = String(v);
  }
  return out;
}

export interface EncodeItemOptions {
  dayDate?: string;
  timeZone?: string | null;
  /** Extra Device Sync fields preserved on round-trip (from prior decode). */
  wireExtras?: Partial<DeviceSyncItem>;
}

/**
 * Encode a web ItineraryItem into iOS Device Sync item dict.
 * Uses `confirmation` (not confirmationNumber) per envelope contract.
 */
export function encodeItem(
  item: ItineraryItem,
  options: EncodeItemOptions = {}
): DeviceSyncItem {
  const extras = options.wireExtras ?? {};
  const dayDate = options.dayDate ?? dateOnlyFromIso(extras.startTime) ?? '1970-01-01';
  const range =
    extras.startTime != null || extras.endTime != null
      ? { startTime: extras.startTime ?? null, endTime: extras.endTime ?? null }
      : isoRangeFromDayTime(dayDate, item.time, item.durationMinutes);

  const metadata: Record<string, string> = {
    ...(extras.metadata ?? {}),
    ...(item.metadata ?? {}),
  };
  if (item.notes) {
    metadata.notes = item.notes;
  }

  // iOS activityDuration: metadata.duration is always seconds (not minutes).
  if (item.type === 'activity') {
    if (
      item.durationMinutes != null &&
      Number.isFinite(item.durationMinutes) &&
      item.durationMinutes > 0
    ) {
      metadata.duration = minutesToMetadataDurationSeconds(item.durationMinutes);
    } else {
      delete metadata.duration;
    }
  }

  return {
    id: item.id,
    type: item.type,
    title: item.title,
    startTime: range.startTime,
    endTime: range.endTime,
    timeZone: options.timeZone ?? extras.timeZone ?? null,
    location:
      extras.location !== undefined
        ? extras.location
        : encodeLocation(item.location, item.address),
    confirmation: item.confirmationNumber ?? extras.confirmation ?? null,
    reservationRequirement:
      item.reservationRequirement ?? extras.reservationRequirement ?? null,
    provider: item.provider ?? extras.provider ?? null,
    phone: item.phone ?? extras.phone ?? null,
    email: item.email ?? extras.email ?? null,
    url: item.url ?? extras.url ?? null,
    reservationUrl: item.reservationUrl ?? extras.reservationUrl ?? null,
    itemDescription: item.notes ?? extras.itemDescription ?? null,
    price: item.price ?? extras.price ?? null,
    currency: item.currency ?? extras.currency ?? null,
    priorityScore: item.priorityScore ?? extras.priorityScore ?? 0,
    metadata,
    reminders: item.reminders ?? extras.reminders ?? [],
    attachments: item.attachments ?? extras.attachments ?? [],
    options: extras.options ?? [],
    barcode: item.barcode !== undefined ? item.barcode : extras.barcode ?? null,
    deletedAt: extras.deletedAt ?? null,
  };
}

/**
 * Decode a Device Sync item dict into web ItineraryItem (+ optional wire extras).
 */
export function decodeItem(raw: unknown): {
  item: ItineraryItem;
  wire: DeviceSyncItem;
} {
  const o = asRecord(raw);
  const type = String(o.type ?? 'other') as ItineraryItemType;
  const startTime = strOrNull(o.startTime);
  const endTime = strOrNull(o.endTime);
  const locationDecoded = decodeLocation(o.location);

  const wire: DeviceSyncItem = {
    id: String(o.id ?? newId()),
    type: String(o.type ?? 'other'),
    title: String(o.title ?? ''),
    startTime,
    endTime,
    timeZone: strOrNull(o.timeZone),
    location: o.location == null ? null : {
      id: String(asRecord(o.location).id ?? newId()),
      address: locationDecoded.location?.address ?? '',
      latitude: locationDecoded.location?.latitude ?? null,
      longitude: locationDecoded.location?.longitude ?? null,
    },
    confirmation: strOrNull(o.confirmation),
    reservationRequirement: strOrNull(o.reservationRequirement),
    provider: strOrNull(o.provider),
    phone: strOrNull(o.phone),
    email: strOrNull(o.email),
    url: strOrNull(o.url),
    reservationUrl: strOrNull(o.reservationUrl),
    itemDescription: strOrNull(o.itemDescription),
    price: numOrNull(o.price),
    currency: strOrNull(o.currency),
    priorityScore: numOrNull(o.priorityScore) ?? 0,
    metadata: encodeMetadata(o.metadata),
    reminders: encodeReminders(o.reminders),
    attachments: encodeAttachments(o.attachments),
    options: encodeItemOptions(o.options),
    barcode: encodeBarcode(o.barcode),
    deletedAt: strOrNull(o.deletedAt),
  };

  const knownTypes: ItineraryItemType[] = [
    'activity',
    'meal',
    'accommodation',
    'transport',
    'note',
    'other',
  ];
  const fromIso = durationMinutesFromIso(startTime, endTime);
  const fromMeta = durationMinutesFromMetadata(wire.metadata);
  const item: ItineraryItem = {
    id: wire.id,
    title: wire.title,
    type: knownTypes.includes(type) ? type : 'other',
    time: hmFromIso(startTime),
    durationMinutes: fromIso ?? fromMeta,
    notes: wire.itemDescription ?? wire.metadata.notes ?? undefined,
    url: wire.url ?? undefined,
    phone: wire.phone ?? undefined,
    email: wire.email ?? undefined,
    provider: wire.provider ?? undefined,
    reservationUrl: wire.reservationUrl ?? undefined,
    reservationRequirement: wire.reservationRequirement ?? undefined,
    price: wire.price,
    currency: wire.currency ?? undefined,
    priorityScore: wire.priorityScore,
    metadata: wire.metadata,
    reminders: wire.reminders,
    attachments: wire.attachments,
    barcode: wire.barcode,
    location: locationDecoded.location
      ? {
          ...locationDecoded.location,
          id: wire.location?.id,
        }
      : undefined,
    address: locationDecoded.address,
    confirmationNumber: wire.confirmation ?? undefined,
  };

  return { item, wire };
}

/** Encode already-wire-shaped item (passthrough normalize). */
export function normalizeDeviceSyncItem(raw: unknown): DeviceSyncItem {
  return decodeItem(raw).wire;
}
